import {
  ActivityIndicator,
  StyleSheet,
  useWindowDimensions,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { PillButton } from '../components/PillButton'
import { useFullHeightStep } from '../flow/flowStepHeight'
import { ReceiveAmountSummary } from '../components/ReceiveAmountSummary'
import {
  FigmaAlertCircleIcon,
  FigmaSuccessCheckIcon,
} from '../icons/FigmaSystemIcons'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import { footerBottomMargin } from '../utils/safeArea'

// The design's own check, larger than any `icon/system` step.
const OUTCOME_ICON_SIZE = 46

/**
 * Where the withdrawal has got to. One value rather than a set of booleans:
 * `settling` and `requested` differ only in whether anything of ours is still
 * being watched, and as flags they could both be set at once.
 */
export type WithdrawalStatus =
  /** Ours is on-chain and still settling. */
  | 'settling'
  /** A venue accepted it; there is nothing of ours left to watch. */
  | 'requested'
  | 'complete'
  /** The relay reported a terminal failure — not merely a slow settle. */
  | 'failed'
  /** Failed, and the funds are on their way back to the sender. */
  | 'refunded'

export interface WithdrawCompleteScreenProps {
  status: WithdrawalStatus
  /** What left the account, for the copy a failure needs. */
  amountLabel: string
  /**
   * What arrived, frozen at submission — the quote is long gone by now, so
   * `undefined` means it was never priced rather than not priced yet.
   */
  usdLabel: string | undefined
  tokenLabel: string | undefined
  onDone: () => void
}

/**
 * The outcome of a submitted withdrawal, on the figure the recipient step ended
 * on: the amount stays where it was and the chrome around it is gone, so
 * arriving here reads as the same screen settling rather than a new one.
 *
 * A missing or slow record reads as "still settling" rather than a failure — it
 * is written best-effort and the funds have already moved — but an explicit
 * terminal failure is shown as one.
 */
export function WithdrawCompleteScreen({
  status,
  amountLabel,
  usdLabel,
  tokenLabel,
  onDone,
}: WithdrawCompleteScreenProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const { height: windowHeight } = useWindowDimensions()
  const height = useFullHeightStep(windowHeight)
  const insets = useSafeAreaInsets()
  // The quote is gone by the time this screen renders, so a figure it never
  // priced is not on its way: lead with what is known — down to what left the
  // account — rather than leaving the step's placeholders standing for good.
  const leadLabel = usdLabel ?? tokenLabel ?? amountLabel
  const secondaryLabel = usdLabel === undefined ? undefined : tokenLabel
  const isFailed = status === 'failed' || status === 'refunded'
  // Only our own transfer is still being watched; a requested one is done here.
  const isSettling = status === 'settling'

  return (
    <Box
      style={{
        height,
        paddingTop: insets.top,
        backgroundColor: theme.background.modal,
      }}
    >
      <Box flex={1} justifyContent="center" style={styles.summary}>
        <ReceiveAmountSummary
          icon={resolveIcon({ theme, status })}
          label={resolveLabel({ t, status })}
          usdLabel={leadLabel}
          tokenLabel={secondaryLabel}
          isQuotePending={false}
        />
        {isFailed ? (
          <Text
            variant="detail"
            style={[styles.detail, { color: theme.content.elements.secondary }]}
          >
            {status === 'refunded'
              ? t('withdrawCrypto.refundedMessage', { amount: amountLabel })
              : t('withdrawCrypto.failedMessage', { amount: amountLabel })}
          </Text>
        ) : null}
      </Box>

      <Box
        style={[
          styles.footer,
          { marginBottom: footerBottomMargin(insets.bottom) },
        ]}
      >
        {isSettling ? (
          <PillButton
            label={t('withdrawAddress.withdrawing')}
            icon={
              <ActivityIndicator
                size="small"
                color={theme.content.button.primary.default}
              />
            }
            disabled
            onPress={onDone}
          />
        ) : (
          <PillButton label={t('common.close')} onPress={onDone} />
        )}
      </Box>
    </Box>
  )
}

type TFunc = ReturnType<typeof useFunkitTranslation>['t']

function resolveIcon({
  theme,
  status,
}: {
  theme: ReturnType<typeof useTheme>
  status: WithdrawalStatus
}) {
  switch (status) {
    case 'failed':
    case 'refunded':
      return (
        <FigmaAlertCircleIcon
          size={OUTCOME_ICON_SIZE}
          color={theme.content.elements.error}
        />
      )
    // Nothing marks it: a check that arrived before the chain had would be a lie.
    case 'settling':
      return undefined
    default:
      return <FigmaSuccessCheckIcon size={OUTCOME_ICON_SIZE} />
  }
}

function resolveLabel({
  t,
  status,
}: {
  t: TFunc
  status: WithdrawalStatus
}): string {
  switch (status) {
    case 'failed':
    case 'refunded':
      return t('withdrawCrypto.failedTitle')
    // Still going: the design leaves the label as it was on the step before, and
    // lets the button carry the news.
    case 'settling':
      return t('withdrawAddress.youllReceive')
    case 'requested':
      return t('withdrawAddress.requested')
    default:
      return t('withdrawAddress.withdrew')
  }
}

const styles = StyleSheet.create({
  // The band the figure is centred in, matching the recipient step's own, so the
  // amount doesn't move when this screen replaces it.
  summary: { paddingHorizontal: 20, paddingVertical: 12, gap: 16 },
  detail: { textAlign: 'center' },
  footer: { marginHorizontal: 20 },
})
